import { Suspense, lazy } from "react";
import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import Footer from "@/components/Footer";

// Lazy load below-fold components to reduce initial JS bundle
const Benefits = lazy(() => import("@/components/Benefits"));
const Comparison = lazy(() => import("@/components/Comparison"));
const ProductShowcase = lazy(() => import("@/components/ProductShowcase"));
const GripQuiz = lazy(() => import("@/components/GripQuiz"));
const FAQ = lazy(() => import("@/components/FAQ"));
const StickyQuizPrompt = lazy(() => import("@/components/StickyQuizPrompt"));

// Minimal loading placeholder for lazy components
const SectionLoader = () => (
  <div className="min-h-[400px] flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const Index = () => {
  // Product Schema for SEO
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "Liquid Chalk Pro Formula",
    description: "Premium liquid chalk for climbing, weightlifting, and gymnastics. Medical-grade magnesium carbonate delivers superior grip that lasts 3x longer than traditional chalk.",
    image: "https://example.com/liquid-chalk.jpg",
    brand: {
      "@type": "Brand",
      name: "GripMax",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      reviewCount: "2847",
    },
    offers: {
      "@type": "Offer",
      url: "https://amzn.to/49KcFQm",
      priceCurrency: "USD",
      price: "14.99",
      availability: "https://schema.org/InStock",
      seller: {
        "@type": "Organization",
        name: "Amazon",
      },
    },
  };

  // WebSite Schema
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "GripMax - Liquid Chalk Guide",
    description: "Your ultimate resource for liquid chalk benefits, comparisons, and recommendations for climbing, weightlifting, and gymnastics.",
    url: "https://gripmax.com",
  };

  // Organization Schema
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "GripMax",
    url: "https://gripmax.com",
    logo: "https://gripmax.com/logo.png",
    sameAs: [],
  };

  return (
    <>
      <Helmet>
        <title>Liquid Chalk: Superior Grip for Climbing, Lifting & Gymnastics | GripMax</title>
        <meta name="description" content="Discover why liquid chalk delivers 3x longer grip than traditional chalk. No mess, gym-friendly, and trusted by 50K+ athletes. Get your perfect grip solution today." />
        <meta name="keywords" content="liquid chalk, climbing chalk, weightlifting chalk, gym chalk, magnesium carbonate, grip enhancer, chalk for deadlifts, rock climbing chalk" />
        <link rel="canonical" href="https://gripmax.com" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Liquid Chalk: Superior Grip for Athletes | GripMax" />
        <meta property="og:description" content="3x longer-lasting grip with zero mess. Discover why 50K+ athletes trust liquid chalk for climbing, lifting, and gymnastics." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://gripmax.com" />
        <meta property="og:image" content="https://gripmax.com/og-image.jpg" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Liquid Chalk: Superior Grip for Athletes | GripMax" />
        <meta name="twitter:description" content="3x longer-lasting grip with zero mess. Discover why 50K+ athletes trust liquid chalk." />
        
        {/* Schema Markup */}
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      </Helmet>

      <div className="min-h-screen">
        <Header />
        <main>
          <Hero />
          <Suspense fallback={<SectionLoader />}>
            <Benefits />
          </Suspense>
          <Suspense fallback={<SectionLoader />}>
            <Comparison />
          </Suspense>
          <Suspense fallback={<SectionLoader />}>
            <ProductShowcase />
          </Suspense>
          <Suspense fallback={<SectionLoader />}>
            <GripQuiz />
          </Suspense>
          <Suspense fallback={<SectionLoader />}>
            <FAQ />
          </Suspense>
        </main>
        <Footer />
        <Suspense fallback={null}>
          <StickyQuizPrompt />
        </Suspense>
      </div>
    </>
  );
};

export default Index;
